import { useEffect, useRef, useState } from 'react';
import { apiFetch, BASE_URL } from '../config/api';
import {
  VISIT_REASONS,
  VISITOR_TYPES,
  createClientRequestId,
  emptyCheckInForm,
  severityLabel,
  validateCheckInForm,
  visitReasonLabel,
  visitorTypeLabel,
} from '../lib/visitorForm';
import { prepareIdScanner, subscribeIdScanner } from '../lib/idScanner';
import { dataUrlToBlob, printVisitorBadge } from '../lib/visitorHardware';
import { cropLicensePortrait } from '../lib/idScanTestUpload';
import VisitorBadgePreview from './VisitorBadgePreview';
import VisitorPortrait from './VisitorPortrait';
import VisitorScreeningPanel, { screeningAllowsCheckIn, screeningApplies } from './VisitorScreeningPanel';

function todayPreview() {
  const now = new Date();
  return {
    dateLabel: now.toLocaleDateString([], { month: 'short', day: '2-digit', year: 'numeric' }),
    timeLabel: now.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }),
  };
}

async function uploadCapture(blob, schoolId, token) {
  const formData = new FormData();
  formData.append('id_image', blob, 'id.jpg');
  const response = await fetch(`${BASE_URL}/visitors/id-captures?school_id=${schoolId}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  const json = await response.json();
  if (!response.ok || !json.success) {
    throw new Error(json.message || 'The ID image could not be saved.');
  }
  return json.data.capture_id;
}

export default function VisitorCheckInModal({
  open,
  schoolId,
  schoolName,
  token,
  scanId = null,
  onClose,
  onComplete,
}) {
  const [form, setForm] = useState(emptyCheckInForm);
  const [destinations, setDestinations] = useState([]);
  const [matches, setMatches] = useState([]);
  const [alertAcknowledged, setAlertAcknowledged] = useState(false);
  const [identityReviewed, setIdentityReviewed] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const [scanMessage, setScanMessage] = useState('Getting the ID scanner ready…');
  const [previewUrl, setPreviewUrl] = useState('');
  const [frontSrc, setFrontSrc] = useState('');
  const [backSrc, setBackSrc] = useState('');
  const portraitRef = useRef('');
  const [captureId, setCaptureId] = useState(null);
  const [identities, setIdentities] = useState([]);
  const [screening, setScreening] = useState(null);
  const [exceptionReason, setExceptionReason] = useState('');
  const [exceptionEnabled, setExceptionEnabled] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [phase, setPhase] = useState('idle');
  const nameRef = useRef(null);
  const requestIdRef = useRef('');
  const documentTokenRef = useRef(null);
  const savedRef = useRef(null);
  const submittingRef = useRef(false);
  const appliedScanId = useRef('');
  const applyScanRef = useRef(async () => {});

  const confirmedDenials = matches.filter((match) => match.match === 'confirmed' && match.severity === 'do_not_admit');
  const confirmedAlerts = matches.filter((match) => match.match === 'confirmed' && match.severity === 'alert_only');
  const possibleMatches = matches.filter((match) => match.match === 'possible');
  const busy = phase === 'saving' || phase === 'checking' || phase === 'printing' || phase === 'scanning';

  useEffect(() => {
    if (!open) return undefined;
    setForm(emptyCheckInForm());
    setMatches([]);
    setAlertAcknowledged(false);
    setIdentityReviewed(false);
    setOverrideReason('');
    setError('');
    setSuccess('');
    setPhase('idle');
    setCaptureId(null);
    setIdentities([]);
    setScreening(null);
    setExceptionReason('');
    setPreviewUrl((current) => {
      if (current) URL.revokeObjectURL(current);
      return '';
    });
    setFrontSrc('');
    setBackSrc('');
    documentTokenRef.current = null;
    savedRef.current = null;
    requestIdRef.current = '';
    const timer = window.setTimeout(() => nameRef.current?.focus(), 0);
    apiFetch(`/visitors/destinations?school_id=${schoolId}`, {}, token)
      .then((response) => setDestinations(response.data || []))
      .catch(() => setDestinations([]));
    apiFetch(`/visitors/screening-settings?school_id=${schoolId}`, {}, token)
      .then((response) => setExceptionEnabled(Boolean(response.data?.screening_exception_enabled)))
      .catch(() => setExceptionEnabled(false));
    return () => window.clearTimeout(timer);
  }, [open, schoolId, token]);

  useEffect(() => () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [busy, onClose, open]);

  useEffect(() => {
    if (!open || form.visitorId || form.fullName.trim().length < 2) {
      setIdentities([]);
      return undefined;
    }
    const timer = window.setTimeout(() => {
      apiFetch(`/visitors/identities?school_id=${schoolId}&q=${encodeURIComponent(form.fullName.trim())}`, {}, token)
        .then((response) => setIdentities(response.data || []))
        .catch(() => setIdentities([]));
    }, 250);
    return () => window.clearTimeout(timer);
  }, [form.fullName, form.visitorId, open, schoolId, token]);

  useEffect(() => {
    if (!open) return undefined;
    appliedScanId.current = '';
    return subscribeIdScanner((scanner) => {
      const scan = scanner.lastScan;
      if (!scan || scan.id === appliedScanId.current) {
        setScanMessage(scanner.message);
        return;
      }
      const openedByThisScan = scan.id === scanId;
      const arrivedWhileOpen = Boolean(appliedScanId.current);
      if (!openedByThisScan && !arrivedWhileOpen) {
        setScanMessage(scanner.message);
        return;
      }
      appliedScanId.current = scan.id;
      applyScanRef.current(scan);
    });
  }, [open, scanId]);

  const preview = todayPreview();

  if (!open) return null;

  function updateField(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
    setSuccess('');
  }

  function chooseDestination(value) {
    const match = destinations.find((destination) => destination.name === value);
    setForm((current) => ({
      ...current,
      destinationLabel: value,
      destinationStaffId: match?.id || null,
    }));
  }

  async function showImage(blob) {
    const url = URL.createObjectURL(blob);
    setPreviewUrl((current) => {
      if (current) URL.revokeObjectURL(current);
      return url;
    });
  }

  async function applyScan(result) {
    if (!result?.scanned) {
      setScanMessage(result?.message || 'The scanner did not return a name or ID image. Enter the visitor manually.');
      return;
    }
    setScanMessage(result.message || 'Scan received. Check the name and birth date before screening.');
    setScreening(null);
    if (result.fullName || result.firstName || result.lastName || result.birthDate) {
      setForm((current) => ({
        ...current,
        fullName: result.fullName || current.fullName,
        firstName: result.firstName || '',
        middleName: result.middleName || '',
        lastName: result.lastName || '',
        suffix: result.suffix || '',
        birthDate: result.birthDate || '',
        identityExtracted: Boolean(result.identityExtracted || (result.firstName && result.lastName && result.birthDate)),
        visitorId: null,
      }));
    }
    documentTokenRef.current = result.documentToken;
    const frontSource = result.frontImageDataUrl || result.imageDataUrl || '';
    const backSource = result.backImageDataUrl || '';
    setFrontSrc(frontSource);
    setBackSrc(backSource);
    let portraitSource = '';
    if (frontSource) {
      try {
        portraitSource = await cropLicensePortrait(frontSource);
      } catch (cropError) {
        portraitSource = '';
      }
    }
    const faceCrop = portraitSource && portraitSource !== frontSource ? portraitSource : '';
    portraitRef.current = faceCrop;
    if (faceCrop) {
      try {
        await showImage(dataUrlToBlob(portraitSource));
      } catch (uploadError) {
        setPreviewUrl('');
      }
    } else {
      setPreviewUrl((current) => {
        if (current) URL.revokeObjectURL(current);
        return '';
      });
    }
    if (frontSource) {
      try {
        const nextCaptureId = await uploadCapture(dataUrlToBlob(frontSource), schoolId, token);
        setCaptureId(nextCaptureId);
      } catch (uploadError) {
        setError(uploadError.message || 'The scanned image could not be saved.');
      }
    }
    nameRef.current?.focus();
  }

  applyScanRef.current = applyScan;

  async function handleScan() {
    if (busy) return;
    setError('');
    setSuccess('');
    await prepareIdScanner();
  }

  async function recordPrint(saved) {
    setPhase('printing');
    const printResult = await printVisitorBadge({
      ...saved.badge,
      portraitDataUrl: portraitRef.current,
    });
    const recorded = await apiFetch(`/visitors/visits/${saved.visit.id}/print-result?school_id=${schoolId}`, {
      method: 'POST',
      body: JSON.stringify({
        school_id: Number(schoolId),
        available: printResult.available,
        accepted: printResult.accepted,
        confirmed: printResult.confirmed,
        message: printResult.message,
      }),
    }, token);
    return recorded.data.print;
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (submittingRef.current) return;
    if (confirmedDenials.length && !overrideReason.trim()) {
      setError('Do not admit. Contact an administrator.');
      return;
    }

    const validation = validateCheckInForm(form);
    if (!savedRef.current && validation) {
      setError(validation);
      return;
    }

    submittingRef.current = true;
    setError('');
    setSuccess('');
    try {
      if (!savedRef.current) {
        if (!requestIdRef.current) requestIdRef.current = createClientRequestId();
        setPhase(screeningApplies(form.visitorType) ? 'checking' : 'saving');
        const response = await apiFetch('/visitors/check-ins', {
          method: 'POST',
          body: JSON.stringify({
            school_id: Number(schoolId),
            client_request_id: requestIdRef.current,
            full_name: form.fullName.trim(),
            first_name: form.firstName.trim(),
            middle_name: form.middleName.trim() || null,
            last_name: form.lastName.trim(),
            suffix: form.suffix.trim() || null,
            birth_date: form.birthDate || null,
            screening_id: screeningApplies(form.visitorType) ? screening?.id || null : null,
            screening_exception_reason: exceptionReason.trim() || null,
            visitor_type: form.visitorType,
            visit_reason: form.visitReason,
            destination_label: form.destinationLabel.trim(),
            destination_staff_id: form.destinationStaffId,
            company: form.company.trim() || null,
            notes: form.notes.trim() || null,
            visitor_id: form.visitorId,
            capture_id: captureId,
            identity_document_token: documentTokenRef.current,
            alert_acknowledged: alertAcknowledged,
            identity_reviewed: identityReviewed,
            override_reason: overrideReason.trim() || null,
          }),
        }, token);
        savedRef.current = response.data;
        setMatches(response.data.matches || []);
      }

      const print = await recordPrint(savedRef.current);
      if (print.status === 'failed' || print.status === 'unavailable') {
        setError(print.message || 'The badge could not be printed.');
        setPhase('idle');
        return;
      }
      onComplete();
      onClose();
    } catch (submitError) {
      const code = submitError.data?.code;
      if (code === 'DO_NOT_ADMIT' || code === 'ALERT_ACK_REQUIRED' || code === 'IDENTITY_REVIEW_REQUIRED') {
        setMatches(submitError.data.matches || []);
        setError(submitError.message);
      } else if (submitError.data?.screening && submitError.data.screening.fbi_status !== 'unable_to_screen' && submitError.data.screening.fbi_status !== 'not_configured') {
        setScreening(submitError.data.screening);
        setError(submitError.message);
      } else if (savedRef.current) {
        setError(submitError.message || 'The visit is saved. Retry printing.');
      } else {
        setError(submitError.message || 'Check-in could not be saved.');
      }
      setPhase('idle');
    } finally {
      submittingRef.current = false;
    }
  }

  const screeningReady = !screeningApplies(form.visitorType)
    || screeningAllowsCheckIn(screening, { exceptionEnabled, exceptionReason });
  const buttonLabel = savedRef.current
    ? 'Retry Print'
    : (confirmedDenials.length ? 'Override and check in' : 'Check In & Print Badge');

  return (
    <div className="vis-modal-overlay" onMouseDown={busy ? undefined : onClose}>
      <form
        className="vis-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="visitor-check-in-title"
        onMouseDown={(event) => event.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <div className="vis-modal-header">
          <div>
            <h2 id="visitor-check-in-title">Check In Visitor</h2>
            {screeningApplies(form.visitorType) ? (
              <p className="vis-screen-note">
                Screened against public government notices.
                <span>Not proof of identity.</span>
              </p>
            ) : null}
          </div>
          <button type="button" className="sa-btn sa-btn-secondary" onClick={onClose} disabled={busy}>Close</button>
        </div>

        <div className="vis-checkin-layout">
          <section className="vis-scan-column">
            <button type="button" className="sa-btn sa-btn-secondary vis-scan-btn" onClick={handleScan} disabled={busy}>
              {phase === 'scanning' ? 'Scanning…' : 'Scan ID'}
            </button>
            <p className="vis-scan-note">{scanMessage}</p>
            <div className="vis-id-pair">
              <figure>
                {frontSrc ? <img src={frontSrc} alt="Front of the ID" /> : <VisitorPortrait src="" />}
                <figcaption>Front</figcaption>
              </figure>
              <figure>
                {backSrc ? <img src={backSrc} alt="Back of the ID" /> : <VisitorPortrait src="" />}
                <figcaption>Back</figcaption>
              </figure>
            </div>
            <VisitorBadgePreview
              schoolName={schoolName}
              visitorType={visitorTypeLabel(form.visitorType)}
              fullName={form.fullName}
              destination={form.destinationLabel}
              visitReason={visitReasonLabel(form.visitReason)}
              dateLabel={preview.dateLabel}
              timeLabel={preview.timeLabel}
              portraitSrc={previewUrl}
            />
            <p className="vis-scan-note">The badge prints on the LabelWriter plugged into this computer.</p>
          </section>

          <section className="vis-fields">
            <label>
              Full name
              <input
                ref={nameRef}
                value={form.fullName}
                onChange={(event) => {
                  const fullName = event.target.value;
                  setScreening(null);
                  setForm((current) => ({
                    ...current,
                    fullName,
                    firstName: '',
                    middleName: '',
                    lastName: '',
                    suffix: '',
                    visitorId: null,
                  }));
                }}
                autoComplete="off"
                required
              />
            </label>
            {form.visitorId ? (
              <div className="vis-inline-note">
                Using the selected visitor record.
                <button type="button" onClick={() => updateField('visitorId', null)}>Clear</button>
              </div>
            ) : null}
            {identities.length ? (
              <div className="vis-match-list">
                <strong>Same name on file. Select someone only if this is the same person.</strong>
                {identities.map((identity) => (
                  <button
                    type="button"
                    key={identity.id}
                    onClick={() => {
                      setForm((current) => ({
                        ...current,
                        visitorId: identity.id,
                        fullName: identity.full_name,
                        company: identity.company || current.company,
                      }));
                      setIdentities([]);
                    }}
                  >
                    {identity.full_name}{identity.company ? ` · ${identity.company}` : ''}
                  </button>
                ))}
              </div>
            ) : null}

            {screeningApplies(form.visitorType) ? (
              <label>
                Birth date
                <input
                  type="date"
                  value={form.birthDate}
                  onChange={(event) => {
                    setScreening(null);
                    updateField('birthDate', event.target.value);
                  }}
                />
              </label>
            ) : null}

            <VisitorScreeningPanel
              token={token}
              schoolId={schoolId}
              form={form}
              screening={screening}
              exceptionReason={exceptionReason}
              onExceptionReason={setExceptionReason}
              onScreening={setScreening}
            />

            <label>
              Visitor type
              <select value={form.visitorType} onChange={(event) => updateField('visitorType', event.target.value)}>
                {VISITOR_TYPES.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}
              </select>
            </label>
            <label>
              Visit reason
              <select value={form.visitReason} onChange={(event) => updateField('visitReason', event.target.value)}>
                {VISIT_REASONS.map((reason) => <option key={reason.value} value={reason.value}>{reason.label}</option>)}
              </select>
            </label>
            <label>
              Visiting / destination
              <input
                list="visitor-destinations"
                value={form.destinationLabel}
                onChange={(event) => chooseDestination(event.target.value)}
                placeholder="Optional. Staff member, office, or department"
              />
              <datalist id="visitor-destinations">
                {destinations.map((destination) => (
                  <option key={destination.id} value={destination.name}>
                    {destination.title || ''}
                  </option>
                ))}
              </datalist>
            </label>
            <label>
              Company
              <input value={form.company} onChange={(event) => updateField('company', event.target.value)} />
            </label>
            <label>
              Notes
              <textarea value={form.notes} onChange={(event) => updateField('notes', event.target.value)} rows={3} />
            </label>

            {confirmedDenials.length ? (
              <div className="vis-alert vis-alert-block">
                <strong>Do not admit. Contact an administrator.</strong>
                {confirmedDenials.map((match) => (
                  <p key={match.restriction_id}>{match.name}: {match.reason}</p>
                ))}
                <label>
                  Override reason
                  <textarea value={overrideReason} onChange={(event) => setOverrideReason(event.target.value)} rows={2} />
                </label>
              </div>
            ) : null}

            {confirmedAlerts.length ? (
              <div className="vis-alert vis-alert-warn">
                <strong>Alert only</strong>
                {confirmedAlerts.map((match) => (
                  <p key={match.restriction_id}>{match.name}: {match.reason}</p>
                ))}
                <label className="vis-check">
                  <input
                    type="checkbox"
                    checked={alertAcknowledged}
                    onChange={(event) => setAlertAcknowledged(event.target.checked)}
                  />
                  I acknowledge this alert
                </label>
              </div>
            ) : null}

            {possibleMatches.length ? (
              <div className="vis-alert">
                <strong>Possible match — review identity.</strong>
                <p>This is not a confirmed match.</p>
                {possibleMatches.map((match) => (
                  <p key={match.restriction_id}>
                    {match.name} · {severityLabel(match.severity)} · {match.reason}
                  </p>
                ))}
                <label className="vis-check">
                  <input
                    type="checkbox"
                    checked={identityReviewed}
                    onChange={(event) => setIdentityReviewed(event.target.checked)}
                  />
                  I reviewed this identity
                </label>
              </div>
            ) : null}

            {error ? <div className="vis-alert vis-alert-block" role="alert">{error}</div> : null}
            {success ? <div className="vis-alert vis-alert-ok" role="status">{success}</div> : null}
            {phase === 'printing' ? (
              <div className="vis-print-status" role="status">
                Printing badge{form.fullName.trim() ? ` for ${form.fullName.trim()}` : ''}…
              </div>
            ) : null}

            <button
              type="submit"
              className="sa-btn sa-btn-primary vis-submit"
              disabled={busy || !screeningReady || (confirmedAlerts.length > 0 && !alertAcknowledged) || (possibleMatches.length > 0 && !identityReviewed) || (confirmedDenials.length > 0 && overrideReason.trim().length < 3)}
            >
              {phase === 'checking' ? 'Checking…' : phase === 'saving' ? 'Saving…' : phase === 'printing' ? 'Printing…' : buttonLabel}
            </button>
          </section>
        </div>
      </form>
    </div>
  );
}
